/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from 'helpers/store'
import {useTypedSelector} from 'helpers/reducers'
import {closeModal, closeSecondaryModal} from 'helpers/actions'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import {PackagesCartItem} from 'types/entities/PackagesCartItem'
import UiTextarea from 'components/Ui/UiTextarea'
import UiCounter from 'components/Ui/UiCounter'
import UiCurrencyInput from 'components/Ui/UiCurrencyInput'
import {OffersAction} from 'store/offers/OffersAction'
import BookingAction from 'store/booking/BookingAction'
import {toast} from 'utils/toastUtils'
import {Controller, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {currencyNumberFormat} from 'utils/utility'
import {DEFAULT_HALL_ID} from 'helpers/config'
import AddItemToCalculationItemsList from 'components/Offers/AddItemToCalculationModalComponents/AddItemToCalculationItemsList'
import {PackagesItemsPerson} from 'types/entities/PackagesItems'
import {PackageItem} from 'types/entities/PackageItem'
import {PackagesCart} from 'types/entities/PackagesCart'
import Loader from 'components/Loader'
import UiSectionHeader from 'components/Ui/UiSectionHeader'
import PackageUpsellSlideOver from 'pages/Front/Booking/components/PackageUpsell/PackageUpsellSlideOver'
import {getCreditAndUpsellItems} from 'utils/offers/addItemToCalculationUtils'
import {getOfferItemPresentation} from 'utils/offers/offerItemPresentation'
import UiCheckbox from 'components/Ui/UiCheckbox'
import UiRoundedContent from "components/Ui/UiRoundedContent";

const editOfferItemSchema = z.object({
    package_item_id: z.number().min(1, 'Please select an item'),
    quantity: z.number().min(0),
    unit_price: z.number().min(0),
    custom_text: z.string().optional(),
})

type EditOfferItemFormData = z.infer<typeof editOfferItemSchema>

interface EditOfferItemModalProps {
    offerId: number
    item: PackagesCartItem
    packagesCart?: PackagesCart
    onSuccess?: () => void
    closeWithSecondaryModal?: boolean
    openSlideOverOnLoad?: boolean
}

const EditOfferItemModal: React.FC<EditOfferItemModalProps> = ({
    offerId,
    item,
    packagesCart: packagesCartProp,
    onSuccess,
    closeWithSecondaryModal = true,
    openSlideOverOnLoad = false,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const selectedOffer = useTypedSelector((state) => state.offersState.selectedOffer)
    const availablePackageItems = useTypedSelector((state) => state.bookingState.bookingProcess.availablePackageItems)
    const sellerId = useTypedSelector((state) => state.sessionState.session?.uid) || 1

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { isSubmitting, errors },
    } = useForm<EditOfferItemFormData>({
        resolver: zodResolver(editOfferItemSchema),
        defaultValues: {
            package_item_id: item.package_item_id,
            quantity: item.drivers_count_adult + item.drivers_count_child,
            unit_price: (item.price / (item.drivers_count_adult + item.drivers_count_child) || 0) / 100,
            custom_text: item.custom_text || '',
        },
    })

    const [isLoadingItems, setIsLoadingItems] = React.useState(false)
    const [selectedItem, setSelectedItem] = React.useState<PackagesItemsPerson | PackageItem | null>(null)
    const portalRef = useRef<HTMLDivElement>(null)
    const [isSlideOverOpen, setIsSlideOverOpen] = React.useState(false)
    const [showInactiveUpsells, setShowInactiveUpsells] = React.useState(false)

    const packagesCart =
        item.packagesCart ||
        packagesCartProp ||
        selectedOffer?.calculations?.find((calc) => calc.packagesCart?.id === item.packages_cart_id)?.packagesCart ||
        selectedOffer?.packagesCart ||
        null

    const allItems = React.useMemo(() => {
        return [
            ...(availablePackageItems?.packageItems?.adult || []),
            ...(availablePackageItems?.packageItems?.child || []),
            ...getCreditAndUpsellItems(availablePackageItems, packagesCart?.category),
        ]
    }, [availablePackageItems, packagesCart?.category])

    React.useEffect(() => {
        const loadPackages = async () => {
            if (!packagesCart?.category || !packagesCart?.date) return
            setIsLoadingItems(true)
            try {
                await dispatch(
                    BookingAction.fetchPackagesByCategoryAndTimeSlotForSeller(
                        {
                            adults: packagesCart.drivers_count_adult || 0,
                            children: packagesCart.drivers_count_child || 0,
                            date: packagesCart.date,
                            package_category_name: packagesCart.category,
                            hall_id: packagesCart.hall_id ?? DEFAULT_HALL_ID,
                            activity_ids: [1],
                            timeslot_id: null,
                        },
                        sellerId
                    ) as any
                )
            } catch (error) {
                console.error('Failed to load available items:', error)
                toast.error(t('Failed to load available items'))
            } finally {
                setIsLoadingItems(false)
            }
        }

        loadPackages()
    }, [dispatch, packagesCart, sellerId, t])

    React.useEffect(() => {
        if (!selectedItem && allItems.length > 0) {
            const currentItem = allItems.find((entry) => entry.id === item.package_item_id)
            if (currentItem) {
                setSelectedItem(currentItem)
                if (openSlideOverOnLoad) {
                    setIsSlideOverOpen(true)
                }
            }
        }
    }, [allItems, item.package_item_id, selectedItem, openSlideOverOnLoad])

    React.useEffect(() => {
        console.log('Form errors:', errors)
    }, [errors])

    const quantity = watch('quantity')
    const unitPrice = watch('unit_price')
    const totalPrice = quantity * unitPrice
    const selectedItemPresentation = selectedItem ? getOfferItemPresentation(selectedItem) : null

    const handleItemSelect = (nextItem: PackagesItemsPerson | PackageItem) => {
        setSelectedItem(nextItem)
        setValue('package_item_id', nextItem.id)
        setValue('unit_price', (nextItem.price || 0) / 100)
        const itemPresentation = getOfferItemPresentation(nextItem)
        const defaultCustomText = itemPresentation.custom_text || itemPresentation.title
        setValue('custom_text', defaultCustomText)
        setIsSlideOverOpen(true)
    }

    const onSubmit = async (data: EditOfferItemFormData, closeAfterSave: boolean = true) => {
        try {
            await dispatch(
                OffersAction.fetchUpdateOfferItem({
                    offer_id: offerId,
                    packages_cart_item_id: item.id,
                    packages_cart_id: item.packages_cart_id,
                    old_package_item_id: item.package_item_id,
                    package_item_id: data.package_item_id,
                    quantity: data.quantity,
                    unit_price: Math.round(data.unit_price * 100),
                    custom_text: data.custom_text,
                })
            )
            toast.success(t('Item updated'))
            if (closeAfterSave) {
                if (closeWithSecondaryModal) {
                    dispatch(closeSecondaryModal())
                } else {
                    dispatch(closeModal())
                }
            }
            if (onSuccess) onSuccess()
        } catch (e) {
            console.error('Error updating item:', e)
            toast.error(t('Failed to update item'))
        }
    }

    const onError = () => {
        toast.error(t('Please fix validation errors'))
    }

    const handleCloseModal = () => {
        if (closeWithSecondaryModal) {
            dispatch(closeSecondaryModal())
        } else {
            dispatch(closeModal())
        }
    }

    return (
        <UiFormModalShell
            className={'EditOfferItemModal'}
            title={t('Swap Item')}
            showHeader
            bodyClassName="px-6 py-6"
            headerActions={
                <>
                    <UiSectionHeader title={t('Please select your extras')} />
                    <UiCheckbox
                        checked={showInactiveUpsells}
                        name="show_inactive_upsells"
                        label={t('Show inactive upsells')}
                        onChange={(event) => setShowInactiveUpsells(event.target.checked)}
                        className="shrink-0"
                    />
                </>
            }
        >
            <form onSubmit={handleSubmit((data) => onSubmit(data, true), onError)} className="h-full flex flex-col">
                <div ref={portalRef} />
                <div className="CreateOfferModal-content w-full flex flex-col space-y-6 flex-1 min-h-0">
                    <div className="SellerUpsells mx-auto w-full flex flex-col gap-4 h-full min-h-0">
                        {isLoadingItems ? (
                            <div className="flex items-center justify-center h-32">
                                <Loader />
                            </div>
                        ) : allItems.length > 0 ? (
                            <div className="flex flex-col gap-1.5 flex-1 min-h-0 overflow-y-auto pr-2">
                                <AddItemToCalculationItemsList
                                    items={allItems}
                                    packageItemType={item.packageItem.type}
                                    selectedItemId={selectedItem?.id ?? item.package_item_id}
                                    collapseUnselected
                                    includeInactiveUpsells={showInactiveUpsells}
                                    onSelect={handleItemSelect}
                                />
                            </div>
                        ) : (
                            <div className="py-4 text-sm text-center text-gray-500 dark:text-gray-400">
                                {t('No items available for swap')}
                            </div>
                        )}
                        {errors.package_item_id && (
                            <span className="text-xs text-red-500 mt-3 block">{errors.package_item_id.message}</span>
                        )}
                    </div>
                </div>

                {isSlideOverOpen && selectedItem && selectedItemPresentation && (
                    <PackageUpsellSlideOver
                        actionsSlot={
                            <button
                                className="flex-1 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-sm hover:bg-blue-700 transition disabled:opacity-50"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? t('Saving...') : t('Save')}
                            </button>
                        }
                        customTextSlot={
                            <UiRoundedContent className={"EditOfferItemModal"}>
                                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                    <p className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                                        {t('Custom Text')}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const description = selectedItemPresentation.custom_text
                                                || selectedItemPresentation.title
                                            setValue('custom_text', description)
                                        }}
                                        className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                    >
                                        {t('Auto fill')}
                                    </button>
                                </div>
                                <div className="p-3">
                                    <Controller
                                        name="custom_text"
                                        control={control}
                                        render={({ field }) => (
                                            <UiTextarea
                                                {...field}
                                                placeholder={t('Enter custom text...')}
                                                rows={10}
                                                error={errors.custom_text?.message}
                                            />
                                        )}
                                    />
                                </div>
                            </UiRoundedContent>
                        }
                        descriptionHtml={selectedItemPresentation.descriptionHtml}
                        detailsSlot={
                            <UiRoundedContent className={"EditOfferItemModalTwo"}>
                                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-700">
                                    <p className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                                        {t('Item Details')}
                                    </p>
                                </div>
                                <div className="p-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                                    <div className="sm:col-span-1">
                                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            {t('Quantity')}
                                        </span>
                                        <div className="mt-2">
                                            <Controller
                                                name="quantity"
                                                control={control}
                                                render={({ field }) => (
                                                    <UiCounter
                                                        value={field.value}
                                                        onChange={(val) => field.onChange(val === '' ? 0 : val)}
                                                        min={0}
                                                    />
                                                )}
                                            />
                                        </div>
                                        {errors.quantity && (
                                            <span className="text-xs text-red-500 mt-0.5">
                                                {errors.quantity.message}
                                            </span>
                                        )}
                                    </div>
                                    <div className="sm:col-span-1">
                                        <Controller
                                            name="unit_price"
                                            control={control}
                                            render={({ field }) => (
                                                <UiCurrencyInput
                                                    label={t('Unit Price')}
                                                    value={field.value}
                                                    onValueChange={(value: string | undefined) => {
                                                        const numValue = value === undefined ? 0 : parseFloat(value)
                                                        field.onChange(isNaN(numValue) ? 0 : numValue)
                                                    }}
                                                    prefix="CHF "
                                                    decimalsLimit={2}
                                                    error={errors.unit_price?.message}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="sm:col-span-1">
                                        <UiCurrencyInput
                                            label={t('Total Price')}
                                            value={totalPrice}
                                            prefix="CHF "
                                            decimalsLimit={2}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            </UiRoundedContent>
                        }
                        groupLabel={selectedItemPresentation.groupLabel}
                        imageSrc={selectedItemPresentation.imageSrc}
                        inCartCount={item.drivers_count_adult + item.drivers_count_child}
                        isOpen={true}
                        onClose={() => setIsSlideOverOpen(false)}
                        title={selectedItemPresentation.title}
                        totalPrice={currencyNumberFormat(Math.round(totalPrice * 100))}
                        unitPrice={currencyNumberFormat(Math.round(unitPrice * 100))}
                        portalPosition={portalRef.current}
                    />
                )}
            </form>
        </UiFormModalShell>
    )
}

export default EditOfferItemModal
